import React, { useEffect, useRef, useState } from 'react';
import { Tour } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// Above antd's Drawer (default z-index ~1000) and Dropdown, so the tour
// popup + mask never render underneath an open drawer/menu.
const TOUR_Z_INDEX = 1100;

// Waits for a selector to exist (optionally scoped to a root element),
// watching both new elements mounting AND attribute changes on existing
// ones — some of this app's wizard steps reuse the same DOM node across
// steps and just swap its data-tour/content in place, which a childList-only
// observer misses entirely.
function waitForSelector(selector, { timeout = 8000, root = document } = {}) {
  return new Promise((resolve) => {
    if (!selector) { resolve(null); return; }
    const existing = root.querySelector(selector);
    if (existing) { resolve(existing); return; }
    const observer = new MutationObserver(() => {
      const found = root.querySelector(selector);
      if (found) { cleanup(); resolve(found); }
    });
    observer.observe(root === document ? document.body : root, {
      childList: true, subtree: true, attributes: true,
    });
    const timer = setTimeout(() => { cleanup(); resolve(null); }, timeout);
    function cleanup() { observer.disconnect(); clearTimeout(timer); }
  });
}

// Same as above, but for an element that must also not be `disabled` —
// several of this app's "Continue" buttons stay in the DOM the whole time
// and only toggle `disabled` once an async preview/validation resolves.
function waitForClickable(selector, opts) {
  return new Promise((resolve) => {
    const check = () => {
      const el = document.querySelector(selector);
      return el && !el.disabled ? el : null;
    };
    const existing = check();
    if (existing) { resolve(existing); return; }
    const observer = new MutationObserver(() => {
      const found = check();
      if (found) { cleanup(); resolve(found); }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    const timer = setTimeout(() => { cleanup(); resolve(null); }, (opts?.timeout) || 8000);
    function cleanup() { observer.disconnect(); clearTimeout(timer); }
  });
}

// Sets a controlled React input/textarea's value the "real" way (native
// setter + a genuine input event) so React's own onChange fires and state
// actually updates — assigning .value directly is invisible to React.
function setReactValue(el, value) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter ? setter.call(el, value) : (el.value = value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

// A tiny synthetic image so a "Photo" step can be stepped through. Nothing
// this attaches ever reaches the server — these apps only upload on a real
// Submit click, which the tour deliberately never performs itself.
function makePlaceholderFile() {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 8; canvas.height = 8;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0D5C63';
    ctx.fillRect(0, 0, 8, 8);
    canvas.toBlob((blob) => resolve(new File([blob], 'tour-demo-photo.png', { type: 'image/png' })), 'image/png');
  });
}

// Runs one step's real-app action automatically: `action` picks the verb,
// `actionKey` (default: targetKey) is what it acts on, scoped inside the
// element found at targetKey unless it's a standalone selector.
async function performAction(step) {
  const action = step.action;
  if (!action || action === 'none') return;

  switch (action) {
    case 'click': {
      const el = await waitForClickable(step.actionKey || step.targetKey);
      el?.click();
      return;
    }
    case 'clickFirst': {
      const container = await waitForSelector(step.targetKey);
      if (!container) return;
      const inner = await waitForSelector(step.actionKey, { root: container });
      inner?.click();
      return;
    }
    case 'fill': {
      const container = await waitForSelector(step.targetKey);
      const input = container?.querySelector(step.actionKey || 'textarea, input');
      if (input) setReactValue(input, step.actionValue || '');
      return;
    }
    case 'check': {
      const container = await waitForSelector(step.targetKey);
      const box = container?.querySelector(step.actionKey || '.ant-checkbox-input');
      if (box && !box.checked) box.click();
      return;
    }
    case 'selectFirstOption': {
      const trigger = await waitForSelector(step.actionKey || step.targetKey);
      trigger?.click();
      const option = await waitForSelector(
        '.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option:not(.ant-select-item-option-disabled)',
      );
      option?.click();
      return;
    }
    case 'uploadPlaceholder': {
      const container = await waitForSelector(step.targetKey);
      const input = container?.querySelector('input[type="file"]');
      if (input) {
        const file = await makePlaceholderFile();
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    default:
      return;
  }
}

// Drives a fully automated, cross-page walkthrough for one announcement's
// tourSteps: navigates to each step's route, waits for its real target to
// exist, spotlights it, and — on "Next" — performs that step's real action
// itself (click, fill, select, upload) before moving on. The user paces it
// with Next/Back; they never have to touch the real page. The one thing
// this deliberately never does automatically is press a real Submit/Confirm
// button — those steps use action:'none' and just spotlight, waiting for a
// real click.
//
// `cleanupSelector` (optional): clicked once, whenever the tour ends — for
// any reason — so a drawer/modal the tour opened along the way never gets
// left hanging open behind it (e.g. `.ant-drawer-close`).
export function FeatureTourRunner({ steps, onDone, cleanupSelector }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState(0);
  const [targetEl, setTargetEl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const step = steps[current];
  const requestId = useRef(0);
  const finishedRef = useRef(false);

  // Navigate to this step's route (if needed) and wait for its target.
  useEffect(() => {
    const myRequest = ++requestId.current;
    setTargetEl(null);
    setTimedOut(false);
    if (!step) return undefined;
    if (step.route && location.pathname !== step.route) navigate(step.route);
    waitForSelector(step.targetKey, { timeout: 10000 }).then((el) => {
      if (requestId.current !== myRequest) return; // a newer step superseded this wait
      if (el) setTargetEl(el);
      else setTimedOut(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // The ONE path anything ends through — guarded so it only ever runs once.
  // This matters because antd's Tour auto-fires onClose(current) the moment
  // "Finish" is clicked on the last step (rc-tour's internal onFinish wrapper
  // calls handleClose() -> onClose, *in addition to* our own nextButtonProps
  // handler below) — without this guard both fire, each calling markSeen,
  // and the second one loses a race against the first's insert (a real
  // E11000 duplicate-key error was reaching the user before this fix).
  const finish = (completed) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (cleanupSelector) document.querySelector(cleanupSelector)?.click();
    onDone(completed);
  };

  // Fires for every route Tour can end on: the built-in close (X) icon,
  // a mask click, or — per the note above — a Finish click on the last
  // step (rc-tour passes us the index it closed at, so we can tell those
  // apart: closing exactly on the last index means they actually finished).
  const handleTourClose = (closedAtIndex) => {
    finish(closedAtIndex === steps.length - 1);
  };

  const goNext = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await performAction(step);
    } finally {
      setBusy(false);
    }
    // Only advance here for non-last steps. The last step's completion is
    // handled by handleTourClose (see finish()'s doc comment) — calling
    // finish() here too would be the exact double-fire this was rebuilt to
    // avoid; finish()'s own guard would just make this a silent no-op, but
    // leaving it out entirely keeps this function's responsibility single.
    if (steps[current + 1]) setCurrent((c) => c + 1);
  };

  const goBack = () => setCurrent((c) => Math.max(0, c - 1));
  const skipStep = () => {
    if (steps[current + 1]) setCurrent((c) => c + 1);
    else finish(false);
  };

  if (!step) return null;
  const isLast = current === steps.length - 1;

  return (
    <>
      <Tour
        open={!!targetEl}
        current={current}
        zIndex={TOUR_Z_INDEX}
        type="primary"
        onClose={handleTourClose}
        steps={steps.map((s, i) => ({
          title: s.title,
          description: s.description,
          placement: s.placement || 'bottom',
          target: i === current ? () => targetEl : () => null,
          nextButtonProps: {
            children: busy ? 'Working…' : isLast ? "I'll finish this myself" : 'Next',
            onClick: goNext,
          },
          prevButtonProps: i > 0 ? { onClick: goBack } : undefined,
        }))}
      />
      {/* Brief gaps between an action completing and the next real element
          mounting (route swap, an async preview query) have nothing for
          Tour to anchor to — show this instead of a floating empty box. */}
      {!targetEl && (
        <div
          style={{
            position: 'fixed', bottom: 24, right: 24, maxWidth: 300, background: '#0D5C63', color: '#fff',
            padding: '14px 18px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: TOUR_Z_INDEX + 1,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 4 }}>{step.title}</strong>
          <div style={{ marginBottom: 10, opacity: 0.9 }}>
            {timedOut ? "Couldn't find this on the page — you can skip it." : 'One moment…'}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={() => finish(false)}
              style={{ background: 'none', border: 'none', color: '#fff', opacity: 0.75, cursor: 'pointer', fontSize: 12, padding: 0 }}
            >
              Cancel tour
            </button>
            {timedOut && (
              <button
                type="button"
                onClick={skipStep}
                style={{ background: 'none', border: 'none', color: '#fff', opacity: 0.75, cursor: 'pointer', fontSize: 12, padding: 0 }}
              >
                Skip this step
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
